import { isEqual, map, pick } from 'lodash-es';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createAudience } from '../../services/backend/audience';
import {
  audienceTargetsFormDataToApiData,
  DemographicFormData,
  DemographicFormDataValidated,
  formDataToApiData,
  formInboundCensusToApiData,
} from '../../util/demographics';
import { isLucidSurvey } from 'util/surveys';
import { Survey, SurveyQualityCheck } from '../../types/domainModels';
import { SURVEY_STATUSES } from 'constants/surveyStatuses';
import {
  surveyQueries,
  useRelaunchExternalSurvey,
  useUpdateInboundCensus,
  useUpdateSurvey,
} from './surveys';
import {
  surveyAudienceTargetQueries,
  useCreateSurveyAudienceTargetBulk,
  useDeleteSurveyAudienceTarget,
  useUpdateSurveyAudienceTarget,
} from './surveyAudienceTarget';
import { UpdateSurveyRequestData } from '../../services/backend/surveys';

export function useSaveAudience({
  initialValues,
  onError,
  onSuccess,
  showTargetByGeoFeature,
  survey,
}: {
  initialValues: DemographicFormData;
  onError(err: Error): void;
  onSuccess(formData: DemographicFormDataValidated): void;
  showTargetByGeoFeature: boolean;
  survey: Survey;
}) {
  const queryClient = useQueryClient();

  const { mutateAsync: relaunchExternalSurvey } = useRelaunchExternalSurvey();
  const { mutateAsync: updateSurvey } = useUpdateSurvey();
  const { mutateAsync: updateInboundCensus } = useUpdateInboundCensus();
  const { mutateAsync: createSurveyAudienceTargetsBulk } =
    useCreateSurveyAudienceTargetBulk();
  const { mutateAsync: updateSurveyAudienceTargets } =
    useUpdateSurveyAudienceTarget();
  const { mutateAsync: deleteSurveyAudienceTarget } =
    useDeleteSurveyAudienceTarget();

  return useMutation({
    mutationFn: async (formData: DemographicFormDataValidated) => {
      const isBringYourOwnAudience =
        formData.audienceProfile.audienceSource === 'byoa';
      let commonSurveyUpdateData: UpdateSurveyRequestData = {
        incidenceTypeId: survey.incidenceTypeId,
        isBringYourOwnAudience,
        panelProvider: isBringYourOwnAudience
          ? null
          : formData.audienceProfile.panelProvider,
        participants: survey.participants,
        projectId: survey.projectId,
        qualityChecks: map(
          formData.respondentQuality.qualityChecks,
          (enabled, type) => {
            return { enabled, type: type as SurveyQualityCheck['type'] };
          },
        ),
        selectedCountry: formData.selectedCountry,
        statusId: survey?.statusId ?? 6,
        title: survey.title,
      };

      if (formData.audienceProfile.audienceType === 'custom') {
        if (showTargetByGeoFeature) {
          const hasAudienceTargetsChanged = !isEqual(
            formData.audienceProfile.audienceTargets,
            initialValues.audienceProfile.audienceTargets,
          );

          if (hasAudienceTargetsChanged) {
            const currentTargetIds = new Set(
              formData.audienceProfile.audienceTargets
                .flatMap(({ surveyAudienceTypes }) => surveyAudienceTypes)
                .map(({ id }) => id),
            );

            const deletedTargetIds =
              initialValues.audienceProfile.audienceTargets
                .flatMap(({ surveyAudienceTypes }) => surveyAudienceTypes)
                .map(({ id }) => id)
                .filter((id) => id !== 0 && !currentTargetIds.has(id));

            const newAudienceTargets = formData.audienceProfile.audienceTargets
              .map((bucket) => ({
                ...bucket,
                surveyAudienceTypes: bucket.surveyAudienceTypes.filter(
                  (target) => target.id === 0,
                ),
              }))
              .filter((bucket) => bucket.surveyAudienceTypes.length > 0);

            const audienceTargetBodies = audienceTargetsFormDataToApiData({
              audienceTargets: newAudienceTargets,
              surveyId: survey.id,
            });

            if (audienceTargetBodies.length > 0) {
              await createSurveyAudienceTargetsBulk({
                data: audienceTargetBodies,
              });
            }

            const initialTargetsById = new Map(
              initialValues.audienceProfile.audienceTargets
                .flatMap(({ surveyAudienceTypes }) => surveyAudienceTypes)
                .filter(({ id }) => id !== 0)
                .map((target) => [target.id, target]),
            );

            const changedAudienceTargets =
              formData.audienceProfile.audienceTargets
                .map((bucket) => ({
                  ...bucket,
                  surveyAudienceTypes: bucket.surveyAudienceTypes.filter(
                    (target) => {
                      const initialTarget = initialTargetsById.get(target.id);
                      return (
                        target.id !== 0 &&
                        initialTarget !== undefined &&
                        !isEqual(target, initialTarget)
                      );
                    },
                  ),
                }))
                .filter((bucket) => bucket.surveyAudienceTypes.length > 0);

            const updatedAudienceTargetBodies =
              audienceTargetsFormDataToApiData({
                audienceTargets: changedAudienceTargets,
                surveyId: survey.id,
              });

            await Promise.all([
              ...deletedTargetIds.map((surveyAudienceTargetId) =>
                deleteSurveyAudienceTarget({ surveyAudienceTargetId }),
              ),
              ...updatedAudienceTargetBodies.map((target) =>
                updateSurveyAudienceTargets({
                  data: target,
                  surveyAudienceTargetId: target.id,
                }),
              ),
            ]);
          }
        } else {
          const customAudienceFields = [
            'audienceProfile.demographicQuotas',
            'audienceProfile.title',
          ];
          const hasCustomAudienceChanged = !isEqual(
            pick(formData, customAudienceFields),
            pick(initialValues, customAudienceFields),
          );

          if (hasCustomAudienceChanged) {
            const newAudienceData = await createAudience({
              data: formDataToApiData({ formData }),
            });
            commonSurveyUpdateData = {
              ...commonSurveyUpdateData,
              audienceId: newAudienceData.id,
            };
          }
        }
      } else if (
        formData.audienceProfile.audienceType === 'natural' &&
        survey.audienceId !== 0
      ) {
        commonSurveyUpdateData = { ...commonSurveyUpdateData, audienceId: 0 };
      }

      await updateSurvey({ data: commonSurveyUpdateData, surveyId: survey.id });

      if (
        survey.status?.name === SURVEY_STATUSES.APPROVED.name &&
        isLucidSurvey(survey)
      ) {
        await relaunchExternalSurvey({ surveyId: survey.id });
      }

      await updateInboundCensus({
        data: formInboundCensusToApiData({ formData }),
        surveyId: survey.id,
      });
    },
    onError,
    onSuccess: async (_data, formData) => {
      // Awaited together so `isPending` doesn't flip to false — and AudienceStep doesn't
      // unfreeze its cached initialValues — until survey and audienceTargets are both fresh.
      await Promise.all([
        queryClient.invalidateQueries(
          surveyQueries.survey({ surveyId: survey.id }),
        ),
        queryClient.invalidateQueries({
          queryKey: surveyQueries.allAudienceSlices({ surveyId: survey.id }),
        }),
        queryClient.invalidateQueries({
          queryKey: surveyAudienceTargetQueries.bySurveyId({
            surveyId: survey.id,
          }).queryKey,
        }),
      ]);

      onSuccess(formData);
    },
  });
}
